/**
 * Generating Placeholder V2
 *
 * Animierter Platzhalter während der Bildgenerierung.
 * Zeigt rotierende Nachrichten mit Timer und Progress-Estimation.
 */

import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { getLoadingMessages } from '@/data/generation-loading-messages';
import { useTheme } from '@/contexts/ThemeContext';
import { useGenerationStats } from '@/hooks/useGenerationStats';
import { animationKeyframes } from './GeneratingPlaceholder/animations';
import { NebulaBackground } from './GeneratingPlaceholder/NebulaBackground';
import { AnimatedContent } from './GeneratingPlaceholder/AnimatedContent';
import { ProgressDisplay } from './GeneratingPlaceholder/ProgressDisplay';

function getRandomMessages(count: number): string[] {
  const shuffled = [...getLoadingMessages()].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

export interface GeneratingPlaceholderProps {
  startTime?: Date;
  className?: string;
  overlay?: boolean;
}

export function GeneratingPlaceholder({ startTime, className, overlay = false }: GeneratingPlaceholderProps) {
  const { theme } = useTheme();
  const [elapsed, setElapsed] = useState(0);
  const [messages] = useState(() => getRandomMessages(20));
  const [messageIndex, setMessageIndex] = useState(0);
  const messageIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: stats } = useGenerationStats();
  const averageDuration = stats?.average_duration ?? 0;
  const hasStats = averageDuration > 0;
  const logoPath = theme === 'dark' ? '/images/logo-dark.svg' : '/images/logo-white.svg';

  useEffect(() => {
    if (!startTime) return;
    const interval = setInterval(() => {
      const diff = Math.floor((new Date().getTime() - startTime.getTime()) / 1000);
      setElapsed(diff);
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  useEffect(() => {
    messageIntervalRef.current = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % messages.length);
    }, 3000);
    return () => {
      if (messageIntervalRef.current) clearInterval(messageIntervalRef.current);
    };
  }, [messages.length]);

  return (
    <div
      className={cn(
        'relative overflow-hidden',
        overlay ? 'absolute inset-0' : 'aspect-square rounded-xl',
        overlay
          ? 'bg-gradient-to-br from-orange-50/95 via-amber-50/95 to-orange-100/95 dark:from-neutral-900/90 dark:via-neutral-900/90 dark:to-neutral-950/92'
          : 'bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 dark:from-orange-950/40 dark:via-amber-950/30 dark:to-orange-900/40',
        !overlay && 'border-2 border-dashed border-orange-200 dark:border-orange-800',
        className
      )}
    >
      <NebulaBackground theme={theme} />

      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10">
        <AnimatedContent logoPath={logoPath} currentMessage={messages[messageIndex]} />
        <ProgressDisplay elapsed={elapsed} averageDuration={averageDuration} hasStats={hasStats} />
      </div>

      <div
        className={cn(
          'absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent dark:via-white/10 -translate-x-full'
        )}
        style={{ animation: 'shimmer 2s infinite' }}
      />

      <style>{animationKeyframes}</style>
    </div>
  );
}

export default GeneratingPlaceholder;
